import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import { isNullish } from "utils/utils";

import moment from "moment";

import {
  queryDisputeDetail,
  queryPayment,
  queryMerchant,
  createDisputeCase,
  updateDisputeCase,
} from "../model";
import s from "./DisputeDetail.module.scss";

import Spin from "components/Spin/Spin";
import Divider from "components/Divider/Divider";

import { searchErrorHandler } from "utils/formValidation";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Datepicker from "components/Input/Datepicker/Datepicker";
import Selection from "components/Input/Selection/Selection";
import { PortalContext } from "App";

import ChargebackStatusOptions from "utils/CompliancePageOptions/ChargebackStatusOptions";
import Compliance from "routes/compliance";
import transactions from "routes/transactions";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import dayjs from "dayjs";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";

const PAGE_LOCALE_KEY = "page.compliance.dispute.detail.";

const INPUT_FIELD_INIT_STATE = {
  reported: "",
  paymentId: "",
  chargebackAmount: "",
  chargebackCode: "",
  reason: "",
  remarks: "",
  status: "PENDING",
};

const INPUT_FIELD_CREATE_STATE = () => ({
  ...INPUT_FIELD_INIT_STATE,
  reported: moment().startOf("day").toISOString(),
});

const PAYMENT_DETAIL_INIT_STATE = {
  merchantName: "",
  cardType: "",
  transactionDate: "",
  transactionAmount: "",
};

const ERROR_INIT_STATE = {
  reported: false,
  paymentId: false,
  chargebackAmount: false,
};

const DisputeDetail = ({ path, redirectFn }) => {
  const [caseId, setCaseId] = useState(null);
  const [caseCreateFlag, setCaseCreateFlag] = useState(true);

  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const stateContext = useContext(PortalContext);
  const { detailPageState } = stateContext;

  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(true);

  const [caseFieldInput, setCaseFieldInput] = useState(INPUT_FIELD_INIT_STATE);
  const [caseFieldPaymentDetail, setcaseFieldPaymentDetail] = useState(
    PAYMENT_DETAIL_INIT_STATE,
  );
  const [focus, setFocus] = useState({});
  const [error, setError] = useState(ERROR_INIT_STATE);

  const [submitting, setSubmitting] = useState(false);

  const [isContinueDialogOpen, setIsContinueDialogOpen] = useState(false);

  const isClosedCase = caseFieldInput?.status?.includes("CLOSED");
  const statusOptions = ChargebackStatusOptions(
    isClosedCase ? "closed" : "non-closed",
  );

  useEffect(() => {
    const sessionState = JSON.parse(sessionStorage.getItem("state"));
    if (detailPageState && detailPageState[path]) {
      setCaseCreateFlag(detailPageState[path].createFlag);
      setCaseId(detailPageState[path].id);
    } else if (sessionState && sessionState[path]) {
      setCaseCreateFlag(sessionState[path].createFlag);
      setCaseId(sessionState[path].id);
    } else {
      setCaseCreateFlag(true);
      setCaseId("");
    }
  }, [detailPageState[path]]);

  useEffect(() => {
    if (caseId != null) {
      if (caseCreateFlag) {
        //create case
        setCaseFieldInput(INPUT_FIELD_CREATE_STATE());
        setcaseFieldPaymentDetail(PAYMENT_DETAIL_INIT_STATE);
        const payload = {
          name: "Create Dispute",
          path: path,
        };
        redirectFn("update", payload);
        setIsLoadingDetail(false);
        setIsLoadingPayments(false);
      } else {
        // fetch detail for edit case
        initialFetchDetails();
      }
    } else {
      console.log("null");
    }
  }, [caseId, caseCreateFlag]);

  const initialFetchDetails = async () => {
    setIsLoadingDetail(true);
    const apiResponse = await queryDisputeDetail(caseId, {});
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            const responseData = responseBody.data;
            setcaseFieldPaymentDetail((state) => ({
              ...state,
              merchantName: responseData.merchant,
              cardType: responseData.cardType,
              transactionDate: responseData.transactionDate,
              transactionAmount: responseData.transactionAmount,
            }));
            setCaseFieldInput({
              paymentId: responseData.paymentId,
              reported: responseData.reported,
              chargebackAmount: responseData.chargebackAmount,
              chargebackCode: responseData.chargebackCode,
              status: responseData.status,
              reason: responseData.reason ?? "",
              remarks: responseData.remarks ?? "",
            });
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "fail.fetchDispute"), "error");
          }
        }
      }
    }

    setIsLoadingDetail(false);
  };

  const fetchPaymentDetails = async (paymentId) => {
    if (!paymentId) {
      setError({ ...error, paymentId: true });
      return;
    }
    setIsLoadingPayments(true);
    const apiResponse = await queryPayment({ id: paymentId });

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            console.log("lookup ", responseBody);
            const paymentDetail = responseBody.data[0];
            if (paymentDetail) {
              let merchantName = paymentDetail.merchantId;
              const response = await queryMerchant({
                merchantId: paymentDetail.merchantId,
              });
              if (response?.responseCode === "0000") {
                const merchant = response?.data;
                if (merchant) {
                  merchantName = `${paymentDetail.merchantId} - ${merchant.name}`;
                }
              }
              setcaseFieldPaymentDetail((state) => ({
                ...state,
                payment: paymentDetail,
                merchantName: merchantName,
                cardType: paymentDetail.cardType,
                transactionDate: paymentDetail.created,
                transactionAmount: paymentDetail.total,
              }));
              setError((prev) => ({
                ...prev,
                paymentId: false,
              }));
            } else {
              snackbar(t(PAGE_LOCALE_KEY + "fail.fetchPayment"), "error");
            }
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "fail.fetchPayment"), "error");
          }
        }
      }
    }

    setIsLoadingPayments(false);
  };
  // ===================== Detail field function start ===================== //

  const textInputOnChange = (e, field, callback) => {
    setError({ ...error, [field]: false });
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setCaseFieldInput({ ...caseFieldInput, [field]: value });
  };

  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const textInputOnBlur = (field, callback) => {
    if (callback) {
      const isValidValue = callback(caseFieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };

  const isNotEmpty = (txt) => {
    if (isNullish(txt)) return false;
    if (txt == "") return false;
    return true;
  };

  const isValidInteger = (value) => {
    return (
      isNotEmpty(value) &&
      Number(value) === parseInt(value) &&
      Number(value) > 0
    );
  };

  const isValidFloat = (value) => {
    return (
      isNotEmpty(value) &&
      Number(value) === parseFloat(value) &&
      /^[\d.]+$/.test(value) &&
      Number(value) > 0
    );
  };

  const isChargebackAmountValid = (e) => {
    if (!isValidFloat(e)) return false;
    const [integerLength, decimalLength] = String(e)
      .split(".")
      .map((e) => e?.length);
    return (
      Number(e) > 0 &&
      Number(e) <= 99999999.99 &&
      (decimalLength ?? 0) <= 2 &&
      (integerLength ?? 0) >= 1
    );
  };

  const isChargebackCodeValid = (e) => {
    return isNotEmpty(e) && e?.length <= 10;
  };

  const isDetailsValid = (e) => {
    return e?.length <= 255;
  };

  const reportedDateOnChange = (e) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    let date = moment(rawDate).isValid()
      ? moment(rawDate)
      : e !== null
        ? ""
        : null;

    date = moment(rawDate).isValid() ? date.startOf("day").toISOString() : date;
    setError({
      ...error,
      reported: false,
    });
    setCaseFieldInput({ ...caseFieldInput, reported: date });
  };
  // ===================== detail field function end ===================== //

  // ===================== detail function  ===================== //
  const handleSendDetailUpdate = async () => {
    const { errorObj, hasError } = searchErrorHandler(
      { ...caseFieldInput, ...caseFieldPaymentDetail },
      [
        (input) =>
          !input.reported && {
            reported: true,
          },

        (input) =>
          (!input.paymentId ||
            !isValidInteger(input.paymentId) ||
            input.merchantName === "") && {
            paymentId: true,
          },

        (input) =>
          !isChargebackAmountValid(input.chargebackAmount) && {
            chargebackAmount: true,
          },

        (input) =>
          !isChargebackCodeValid(input.chargebackCode) && {
            chargebackCode: true,
          },

        (input) =>
          !isDetailsValid(input.reason) && {
            reason: true,
          },

        (input) =>
          !isDetailsValid(input.remarks) && {
            remarks: true,
          },
      ],
    );
    setError(errorObj);
    if (hasError) return;

    sendDetailUpdate();
  };
  const sendDetailUpdate = async () => {
    setSubmitting(true);
    const payload = !isClosedCase
      ? { ...caseFieldInput }
      : { remarks: caseFieldInput.remarks };

    if (caseId === "") {
      const response = await createDisputeCase(payload);

      if (!response || !response?.data) {
        snackbar(t(PAGE_LOCALE_KEY + "fail.createDispute"), "error");
      } else {
        const responseBody = response.data;
        switch (response.status) {
          case 401: {
            console.log("You have no authorization to view the page");
            return logout("TOKEN_EXPIRED");
          }
          case 403: {
            snackbar(t("general.message.unauthorized"), "error");
            break;
          }
          default: {
            if (responseBody.responseCode === "0000") {
              snackbar(t(PAGE_LOCALE_KEY + "success.createDispute"), "success");
              setCaseFieldInput(INPUT_FIELD_CREATE_STATE());
              setcaseFieldPaymentDetail(PAYMENT_DETAIL_INIT_STATE);
              refreshList();
              setIsContinueDialogOpen(true);
            } else {
              snackbar(t(PAGE_LOCALE_KEY + "fail.createDispute"), "error");
            }
          }
        }
      }
    } else {
      const response = await updateDisputeCase(caseId, payload);
      if (!response || !response?.data) {
        snackbar(t(PAGE_LOCALE_KEY + "fail.updateDispute"), "error");
      } else {
        const responseBody = response.data;
        switch (response.status) {
          case 401: {
            console.log("You have no authorization to view the page");
            return logout("TOKEN_EXPIRED");
          }
          case 403: {
            snackbar(t("general.message.unauthorized"), "error");
            break;
          }
          default: {
            if (responseBody.responseCode === "0000") {
              snackbar(t(PAGE_LOCALE_KEY + "success.updateDispute"), "success");

              //order of execution is important, close -> redirect
              redirectFn("close", { tabId: path });
              redirectListAndRefresh();
            } else {
              snackbar(t(PAGE_LOCALE_KEY + "fail.updateDispute"), "error");
            }
          }
        }
      }
    }
    setSubmitting(false);
  };

  const refreshList = () => {
    const newpath = "/compliance/disputeList/DisputeList";
    redirectFn("refresh", {
      path: newpath,
    });
  };

  const redirectListAndRefresh = () => {
    const newpath = "/compliance/disputeList/DisputeList";
    const payload = {
      name: Compliance.find((tab) => tab.path === newpath).tabName,
      path: newpath,
      refresh: "refresh",
    };
    redirectFn("redirectPage", payload);
  };

  const RoleDetailContent = () => {
    return (
      <div className={s.detailsContainer}>
        <div className={s.inputGridSmall}>
          <Datepicker
            label={t(PAGE_LOCALE_KEY + "columns.reported.label")}
            placeholder={t(PAGE_LOCALE_KEY + "columns.reported.placeholder")}
            type="start"
            limitValue={dayjs()}
            minDate={dayjs("2022-01-01")}
            value={caseFieldInput.reported}
            onchangeFn={reportedDateOnChange}
            onfocusFn={() => textInputOnFocus("reported")}
            onblurFn={() => textInputOnBlur("reported", isNotEmpty)}
            sxParams={{
              datepickerFocus: focus.reported,
              datepickerError: error.reported,
            }}
            errorMessage={t(PAGE_LOCALE_KEY + "columns.reported.errorInput")}
            disabled={isClosedCase}
            mandatory
            readonly
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "columns.paymentId.label")}
            placeholder={t(PAGE_LOCALE_KEY + "columns.paymentId.placeholder")}
            value={caseFieldInput.paymentId}
            onchangeFn={(e) => {
              textInputOnChange(e, "paymentId");
              setcaseFieldPaymentDetail(PAYMENT_DETAIL_INIT_STATE);
            }}
            onEnterDownFn={() => fetchPaymentDetails(caseFieldInput.paymentId)}
            onfocusFn={() => textInputOnFocus("paymentId")}
            onblurFn={() => textInputOnBlur("paymentId", isValidInteger)}
            sxParams={{
              textFocus: focus.paymentId,
              textError: error.paymentId,
            }}
            errorMessage={t(PAGE_LOCALE_KEY + "columns.paymentId.errorInput")}
            disabled={!caseCreateFlag}
            mandatory
            endAttachment={
              <ButtonMAS
                disabled={submitting || isLoadingPayments || isLoadingDetail}
                onClick={() => {
                  if (caseCreateFlag) {
                    fetchPaymentDetails(caseFieldInput.paymentId);
                  } else {
                    const path = "/transactions/offline-payments-details";
                    const paymentId = caseFieldInput.paymentId;
                    const payload = {
                      name: `${transactions.find((tab) => tab.path === path).tabName
                        } #${paymentId}`,
                      path: path,
                      state: {
                        id: paymentId,
                      },
                    };
                    redirectFn("redirectPage", payload);
                  }
                }}
                nowrap
              >
                {t(
                  PAGE_LOCALE_KEY +
                  (caseCreateFlag ? "button.lookup" : "button.details"),
                )}
              </ButtonMAS>
            }
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "columns.merchantName.label")}
            placeholder={t(
              PAGE_LOCALE_KEY + "columns.merchantName.placeholder",
            )}
            value={caseFieldPaymentDetail.merchantName}
            onchangeFn={(e) => textInputOnChange(e, "merchantName")}
            onfocusFn={() => textInputOnFocus("merchantName")}
            onblurFn={() => textInputOnBlur("merchantName", isNotEmpty)}
            sxParams={{
              textFocus: focus.merchantName,
              textError: error.merchantName,
            }}
            errorMessage={t(
              PAGE_LOCALE_KEY + "columns.merchantName.errorInput",
            )}
            disabled
          />

          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "columns.cardType.label")}
            placeholder={t(PAGE_LOCALE_KEY + "columns.cardType.placeholder")}
            value={caseFieldPaymentDetail.cardType}
            onchangeFn={(e) => textInputOnChange(e, "cardType")}
            onfocusFn={() => textInputOnFocus("cardType")}
            onblurFn={() => textInputOnBlur("cardType", isNotEmpty)}
            sxParams={{ textFocus: focus.cardType, textError: error.cardType }}
            errorMessage={t(PAGE_LOCALE_KEY + "columns.cardType.errorInput")}
            disabled
          />

          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "columns.transactionDate.label")}
            placeholder={t(
              PAGE_LOCALE_KEY + "columns.transactionDate.placeholder",
            )}
            value={
              caseFieldPaymentDetail.transactionDate &&
              moment(caseFieldPaymentDetail.transactionDate).format(
                "YYYY-MM-DD HH:mm:ss Z",
              )
            }
            onchangeFn={(e) => textInputOnChange(e, "transactionDate")}
            onfocusFn={() => textInputOnFocus("transactionDate")}
            onblurFn={() => textInputOnBlur("transactionDate", isNotEmpty)}
            sxParams={{
              textFocus: focus.transactionDate,
              textError: error.transactionDate,
            }}
            errorMessage={t(
              PAGE_LOCALE_KEY + "columns.transactionDate.errorInput",
            )}
            disabled
          />

          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "columns.transactionAmount.label")}
            placeholder={t(
              PAGE_LOCALE_KEY + "columns.transactionAmount.placeholder",
            )}
            value={caseFieldPaymentDetail.transactionAmount}
            onchangeFn={(e) => textInputOnChange(e, "transactionAmount")}
            onfocusFn={() => textInputOnFocus("transactionAmount")}
            onblurFn={() => textInputOnBlur("transactionAmount", isNotEmpty)}
            sxParams={{
              textFocus: focus.transactionAmount,
              textError: error.transactionAmount,
            }}
            errorMessage={t(
              PAGE_LOCALE_KEY + "columns.transactionAmount.errorInput",
            )}
            disabled
          />

          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "columns.chargebackAmount.label")}
            placeholder={t(
              PAGE_LOCALE_KEY + "columns.chargebackAmount.placeholder",
            )}
            value={caseFieldInput.chargebackAmount}
            onchangeFn={(e) => textInputOnChange(e, "chargebackAmount")}
            onfocusFn={() => textInputOnFocus("chargebackAmount")}
            onblurFn={() =>
              textInputOnBlur("chargebackAmount", isChargebackAmountValid)
            }
            sxParams={{
              textFocus: focus.chargebackAmount,
              textError: error.chargebackAmount,
            }}
            errorMessage={t(
              PAGE_LOCALE_KEY + "columns.chargebackAmount.errorInput",
            )}
            disabled={isClosedCase}
            mandatory
          />
          <OutlinedText
            label={t(PAGE_LOCALE_KEY + "columns.chargebackCode.label")}
            placeholder={t(
              PAGE_LOCALE_KEY + "columns.chargebackCode.placeholder",
            )}
            value={caseFieldInput.chargebackCode}
            onchangeFn={(e) => textInputOnChange(e, "chargebackCode")}
            onfocusFn={() => textInputOnFocus("chargebackCode")}
            onblurFn={() =>
              textInputOnBlur("chargebackCode", isChargebackCodeValid)
            }
            sxParams={{
              textFocus: focus.chargebackCode,
              textError: error.chargebackCode,
            }}
            errorMessage={t(
              PAGE_LOCALE_KEY + "columns.chargebackCode.errorInput",
            )}
            disabled={isClosedCase}
            mandatory
          />
        </div>

        <div className={s.inputGridBig}>
          <OutlinedText
            multiline
            label={t(PAGE_LOCALE_KEY + "columns.reason.label")}
            placeholder={t(PAGE_LOCALE_KEY + "columns.reason.placeholder")}
            value={caseFieldInput.reason}
            onchangeFn={(e) => textInputOnChange(e, "reason")}
            onfocusFn={() => textInputOnFocus("reason")}
            onblurFn={() => textInputOnBlur("reason", isDetailsValid)}
            sxParams={{ textFocus: focus.reason, textError: error.reason }}
            errorMessage={t(PAGE_LOCALE_KEY + "columns.reason.errorInput")}
            disabled={isClosedCase}
          />

          <OutlinedText
            multiline
            label={t(PAGE_LOCALE_KEY + "columns.remarks.label")}
            placeholder={t(PAGE_LOCALE_KEY + "columns.remarks.placeholder")}
            value={caseFieldInput.remarks}
            onchangeFn={(e) => textInputOnChange(e, "remarks")}
            onfocusFn={() => textInputOnFocus("remarks")}
            onblurFn={() => textInputOnBlur("remarks", isDetailsValid)}
            sxParams={{ textFocus: focus.remarks, textError: error.remarks }}
            errorMessage={t(PAGE_LOCALE_KEY + "columns.remarks.errorInput")}
          />
        </div>

        <div className={s.inputGridSmall}>
          <Selection
            label={t(PAGE_LOCALE_KEY + "columns.status.label")}
            placeholder={t(PAGE_LOCALE_KEY + "columns.status.placeholder")}
            selectionValue={caseFieldInput.status}
            onchangeFn={(e) => textInputOnChange(e, "status")}
            onfocusFn={() => textInputOnFocus("status")}
            onblurFn={() => textInputOnBlur("status")}
            options={statusOptions}
            sxParams={{ selectionFocus: focus.status }}
            disabled={isClosedCase}
            mandatory
          />
        </div>
        <div className={s.dialogActionContainer}>
          <ButtonMAS
            disabled={submitting}
            onClick={() => {
              handleSendDetailUpdate();
            }}
          >
            {caseCreateFlag
              ? t(PAGE_LOCALE_KEY + "button.create")
              : t(PAGE_LOCALE_KEY + "button.update")}
          </ButtonMAS>
        </div>
      </div>
    );
  };

  // ===================== detail function end ===================== //

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        <div className={s.detailPage}>
          {(submitting || isLoadingPayments || isLoadingDetail) && (
            <Spin type={"search"} hover />
          )}
          {caseCreateFlag ? (
            <div className={s.header}>
              <span>{t(PAGE_LOCALE_KEY + "sectionTitle.create")}</span>
            </div>
          ) : (
            <div className={s.header}>
              <span>{t(PAGE_LOCALE_KEY + "sectionTitle.edit") + caseId}</span>
            </div>
          )}
          <Divider />
          {RoleDetailContent()}
          <SimpleDialog
            open={isContinueDialogOpen}
            dialogOncloseFn={() => setIsContinueDialogOpen(false)}
            content={t(PAGE_LOCALE_KEY + "continueDialog.title")}
            confirmFn={() => setIsContinueDialogOpen(false)}
            cancelFn={() => {
              //order of execution is important, close -> redirect
              redirectFn("close", { tabId: path });
              redirectListAndRefresh();
            }}
            positiveAction={t("component.simple.dialog.action.1")}
            negativeAction={t(PAGE_LOCALE_KEY + "continueDialog.back")}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(DisputeDetail);
